/**
 * Nether gallery colourway pre-warm (PDP only).
 *
 * THE PROBLEM THIS EXISTS TO SOLVE (owner 2026-09-09: "as you choose a slide, it
 * ends up hotloading the second image instead of the first one every time, which
 * seems a little weird. The gallery kind of glitches out").
 *
 * product-media-gallery-content.liquid marks every image that does NOT belong to
 * the selected colourway `loading="lazy"`. That is what took the PDP from 1383KB
 * to 648KB and it should stay. The cost is that switching colourway then asks the
 * browser for photographs it has never seen, and you watch them arrive.
 *
 * Why the SECOND image specifically, which is the part that looked like a bug:
 * the product's featured image is media index 0, so on ROAM "[Storm 1]" is
 * already in cache from first paint no matter which colourway is selected.
 * "[Storm 2]" onward sit at index 11+ and are lazy. So slide one is instant and
 * slide two is the one that visibly loads — not a sequencing fault, just the only
 * slide that still had to be fetched.
 *
 * The fix is neither "make it lazy" nor "make it eager", it is "make it eager
 * LATER". First paint keeps the light critical path; once the page is loaded and
 * the main thread is idle, the colourways nobody has asked for yet are fetched in
 * the background, so by the time a swatch is tapped they are already in cache.
 *
 * Flipping the attribute on the real <img> rather than fetching into a detached
 * Image() is deliberate: the element keeps its own srcset/sizes, so the browser
 * picks exactly the candidate it would have picked on reveal. A detached copy has
 * no layout, so a `sizes` value in anything but vw units resolves differently and
 * warms the wrong file — a cache miss that looks identical to no preloading.
 *
 * Two at a time, chained on load, so this never becomes a 674KB burst competing
 * with anything the customer is actually waiting for.
 *
 * ONLY WHAT A COLOUR TAP SHOWS FIRST (2026-09-25, the speed pass the owner
 * approved). This used to warm every lazy gallery photo on the page, and on
 * Roam that had grown to 1.6MB after load on a phone: every slide of every
 * colour, an "archive" set no swatch can select, and the hidden desktop grid's
 * copies as well. On the cellular connection most Instagram traffic lands on,
 * that kept the line busy for several seconds right when the customer starts
 * scrolling, opening the sheet or adding to cart. The glitch this file exists
 * for is the first photos of a newly tapped colour arriving late, and those are
 * all it now warms:
 *   - the first LEADS photos of each colour (the lead is its worn photo),
 *   - of colours a swatch can select (data-colorway-key on the colourway dots;
 *     a product with no dots keeps every colour),
 *   - in the gallery layout that is actually on screen (the slideshow on a
 *     phone, the grid on desktop; a slide's own [hidden] does not count).
 * Swiping on through a colour loads ahead as it always has, through the
 * slideshow's own lazy loading. Measured on Roam: 1.6MB down to about 220KB.
 */
(function () {
  'use strict';

  var CONCURRENCY = 2;
  var LEADS = 2;
  var started = false;

  /* Respect an explicit data saver, and skip the whole thing on 2g. This is a
     nicety for a customer who may never switch colourway; it is not worth a
     megabyte of someone's phone plan. */
  function allowed() {
    var c = navigator.connection;
    if (!c) return true;
    if (c.saveData) return false;
    return !/2g/.test(c.effectiveType || '');
  }

  function idle(fn) {
    if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 3000 });
    else setTimeout(fn, 300);
  }

  /* The colours a customer can pick, from the colourway dots. Empty when the
     product has none, which lets every colour through. */
  function selectable() {
    var keys = {};
    document.querySelectorAll('.nether-colorway__dot[data-colorway-key]').forEach(function (dot) {
      keys[dot.getAttribute('data-colorway-key')] = true;
    });
    return keys;
  }

  /* Is the layout this slide belongs to on screen? Walks up from the slide's
     parent, so the slide's own [hidden] (a colour not selected) is ignored and
     only the layout itself counts: the slideshow on a phone, the grid on
     desktop. */
  function layoutShown(slide) {
    for (var n = slide.parentElement; n && n !== document.body; n = n.parentElement) {
      if (getComputedStyle(n).display === 'none') return false;
    }
    return true;
  }

  function pending() {
    var keys = selectable();
    var anyKeys = Object.keys(keys).length > 0;
    var seen = {};
    var queue = [];
    document.querySelectorAll('[data-media-color]').forEach(function (slide) {
      var img = slide.querySelector('img');
      if (!img) return;
      var colour = slide.getAttribute('data-media-color');
      if (anyKeys && !keys[colour]) return;
      if (!layoutShown(slide)) return;
      seen[colour] = (seen[colour] || 0) + 1;
      if (seen[colour] > LEADS) return;
      if (img.getAttribute('loading') === 'lazy') queue.push(img);
    });
    return queue;
  }

  function run() {
    var queue = pending();
    if (!queue.length) return;

    var i = 0;
    function next() {
      if (i >= queue.length) return;
      var img = queue[i++];

      /* Already handled by another pass (the gallery re-renders on variant
         change, so the same node can be seen twice). */
      if (!img || img.getAttribute('loading') !== 'lazy') {
        idle(next);
        return;
      }

      var done = function () {
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        idle(next);
      };
      img.addEventListener('load', done);
      img.addEventListener('error', done);

      /* The load starts here. A display:none image still fetches once it is no
         longer lazy, which is what lets this warm hidden colourways at all. */
      img.setAttribute('loading', 'eager');

      /* Already decoded (served from cache) — no load event will fire. */
      if (img.complete) done();
    }

    for (var n = 0; n < CONCURRENCY; n++) idle(next);
  }

  function start() {
    if (started || !allowed()) return;
    started = true;
    idle(run);
  }

  /* Only after the page has finished its own loading. Anything earlier and this
     is competing with the images the customer is looking at right now. */
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start);

  /* The gallery re-renders on every variant change, which brings back a fresh
     set of lazy images for the colourways still unseen. Re-run, but let the
     morph settle first. `started` guards the initial pass only. */
  document.addEventListener('shopify:section:load', function () {
    if (started) idle(run);
  });
})();
